/**
 * 🎨 STANDARDIZED TOAST NOTIFICATION CONFIGURATION
 *
 * This is the SINGLE SOURCE OF TRUTH for all toast notifications in the application.
 *
 * ⚠️ IMPORTANT: DO NOT use toast.success() or toast.error() from sonner directly!
 * ALWAYS import and use the standardized notifications from this config file.
 *
 * 🎯 Usage:
 * ```typescript
 * import { notifications } from '@/config/toast-notifications';
 *
 * // Success notification
 * notifications.success('Operation successful', 'Your changes have been saved');
 *
 * // Error notification
 * notifications.error('Operation failed', 'Please try again later');
 *
 * // Warning notification
 * notifications.warning('Warning', 'This action may have consequences');
 *
 * // Info notification
 * notifications.info('Information', 'Here is some helpful information');
 * ```
 *
 * 📝 Features:
 * - Branded Lithos logo on all notifications
 * - Gradient backgrounds (green/red/amber/blue)
 * - Auto-dismiss with progress bar
 * - Hover to pause functionality
 * - Consistent sizing and styling
 * - Type-safe TypeScript interfaces
 *
 * 🔧 Configuration:
 * - Success: 2 second duration
 * - Error: 3 second duration
 * - Warning: 2.5 second duration
 * - Info: 2 second duration
 * - Position: Bottom right
 * - Max width: 400px
 * - Min width: 320px
 */

import type { ToastType } from '../client/components/ui/toast-notification.js';

// Import the toast hook - this will be set at runtime
let toastContext: {
  showSuccess: (title: string, message?: string, persistent?: boolean) => void;
  showError: (title: string, message?: string, persistent?: boolean) => void;
  showWarning: (title: string, message?: string, persistent?: boolean) => void;
  showInfo: (title: string, message?: string, persistent?: boolean) => void;
} | null = null;

/**
 * Initialize the toast notification system with the context
 * This is called automatically when the ToastProvider mounts
 *
 * @internal - Do not call this directly
 */
export const initializeNotifications = (context: typeof toastContext) => {
  toastContext = context;
};

/**
 * Get the toast context - throws error if not initialized
 *
 * @internal - Do not call this directly
 */
const getToastContext = () => {
  if (!toastContext) {
    throw new Error(
      'Toast notifications not initialized. Make sure ToastProvider is mounted in your app tree.'
    );
  }
  return toastContext;
};

/**
 * 🎨 STANDARDIZED NOTIFICATION FUNCTIONS
 *
 * These are the ONLY functions you should use for showing notifications.
 * They provide a consistent, branded experience across the entire application.
 */
export const notifications = {
  /**
   * ✅ Show a success notification
   *
   * @param title - Main heading (required)
   * @param message - Optional detailed message
   * @param persistent - If true, toast won't auto-dismiss (default: false)
   *
   * @example
   * notifications.success('Saved!', 'Your changes have been saved successfully');
   */
  success: (title: string, message?: string, persistent: boolean = false) => {
    const context = getToastContext();
    context.showSuccess(title, message, persistent);
  },

  /**
   * ❌ Show an error notification
   *
   * @param title - Main heading (required)
   * @param message - Optional detailed error message
   * @param persistent - If true, toast won't auto-dismiss (default: false)
   *
   * @example
   * notifications.error('Failed to save', 'Network connection error. Please try again.');
   */
  error: (title: string, message?: string, persistent: boolean = false) => {
    const context = getToastContext();
    context.showError(title, message, persistent);
  },

  /**
   * ⚠️ Show a warning notification
   *
   * @param title - Main heading (required)
   * @param message - Optional detailed warning message
   * @param persistent - If true, toast won't auto-dismiss (default: false)
   *
   * @example
   * notifications.warning('Unsaved changes', 'You have unsaved changes that will be lost');
   */
  warning: (title: string, message?: string, persistent: boolean = false) => {
    const context = getToastContext();
    context.showWarning(title, message, persistent);
  },

  /**
   * ℹ️ Show an info notification
   *
   * @param title - Main heading (required)
   * @param message - Optional detailed information
   * @param persistent - If true, toast won't auto-dismiss (default: false)
   *
   * @example
   * notifications.info('New feature', 'Check out our new inventory management tools');
   */
  info: (title: string, message?: string, persistent: boolean = false) => {
    const context = getToastContext();
    context.showInfo(title, message, persistent);
  },
};

/**
 * 📋 TYPE EXPORTS
 *
 * Export types for use throughout the application
 */
export type { ToastType };

/**
 * 🎨 NOTIFICATION PRESETS
 *
 * Common notification messages used throughout the application
 */
export const notificationPresets = {
  // Generic messages
  saved: {
    title: 'Saved successfully',
    message: 'Your changes have been saved',
  },
  deleted: {
    title: 'Deleted successfully',
    message: 'The item has been removed',
  },
  updated: {
    title: 'Updated successfully',
    message: 'The changes have been applied',
  },
  created: {
    title: 'Created successfully',
    message: 'The new item has been created',
  },

  // Error messages
  saveFailed: {
    title: 'Save failed',
    message: 'Unable to save your changes. Please try again.',
  },
  deleteFailed: {
    title: 'Delete failed',
    message: 'Unable to delete the item. Please try again.',
  },
  updateFailed: {
    title: 'Update failed',
    message: 'Unable to update the item. Please try again.',
  },
  createFailed: {
    title: 'Create failed',
    message: 'Unable to create the item. Please try again.',
  },
  networkError: {
    title: 'Network error',
    message: 'Unable to connect to the server. Please check your connection.',
  },
  permissionDenied: {
    title: 'Permission denied',
    message: 'You do not have permission to perform this action.',
  },

  // Validation messages
  validationError: {
    title: 'Validation error',
    message: 'Please check your input and try again.',
  },
  requiredFields: {
    title: 'Required fields missing',
    message: 'Please fill in all required fields.',
  },
};

/**
 * 🔧 HELPER FUNCTIONS
 *
 * Utility functions for common notification patterns
 */
export const notificationHelpers = {
  /**
   * Show a success notification using a preset
   */
  showPresetSuccess: (presetKey: keyof typeof notificationPresets) => {
    const preset = notificationPresets[presetKey];
    notifications.success(preset.title, preset.message);
  },

  /**
   * Show an error notification using a preset
   */
  showPresetError: (presetKey: keyof typeof notificationPresets) => {
    const preset = notificationPresets[presetKey];
    notifications.error(preset.title, preset.message);
  },

  /**
   * Show a notification based on an API response
   * Automatically determines if it's success or error based on response
   */
  showApiResponse: (
    success: boolean,
    successMessage: string,
    errorMessage?: string,
    errorDetails?: string
  ) => {
    if (success) {
      notifications.success(successMessage);
    } else {
      notifications.error(
        errorMessage || 'Operation failed',
        errorDetails || 'Please try again later'
      );
    }
  },
};

export default notifications;
